import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { LanguageLevel } from '../types/database'
import { DataLayer } from '../lib/dataLayer'

interface CardFormProps {
  onSuccess?: () => void
  initialData?: {
    id: string
    front: string
    back: string
    language: string
    level?: LanguageLevel
    tags: string[]
    order?: number | null
  }
}

export function CardForm({ onSuccess, initialData }: CardFormProps) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const [front, setFront] = useState(initialData?.front || '')
  const [back, setBack] = useState(initialData?.back || '')
  const [language, setLanguage] = useState(initialData?.language || 'es')
  const [level, setLevel] = useState<LanguageLevel>(initialData?.level || 'B1')
  const [tagInput, setTagInput] = useState(initialData?.tags?.join(', ') || '')
  const [order, setOrder] = useState<string>(initialData?.order?.toString() || '')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    setError(null)
    setLoading(true)

    const tags = tagInput
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0)

    try {
      const orderValue = order.trim() ? parseInt(order, 10) : null
      
      if (initialData) {
        // Update existing card
        await DataLayer.updateCard(initialData.id, {
          front,
          back,
          language,
          level,
          tags,
          order: orderValue
        }, user.id)
      } else {
        // Create new card
        await DataLayer.createCard({
          front,
          back,
          language,
          level,
          tags,
          order: orderValue,
          source_id: null,
          exercise_id: null,
          audio_url: null
        }, user.id)
      }

      if (onSuccess) onSuccess()
      
      // Reset form if creating new card
      if (!initialData) {
        setFront('')
        setBack('')
        setLanguage('en')
        setTagInput('')
        setOrder('')
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="front" className="block text-sm font-medium text-gray-700">
          Front (Question)
        </label>
        <textarea
          id="front"
          value={front}
          onChange={(e) => setFront(e.target.value)}
          required
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder="Enter the question or prompt..."
        />
        <p className="mt-1 text-sm text-gray-500">
          Use markdown for formatting. Use %text% for hidden translations, $text$ for text-to-speech.
        </p>
      </div>

      <div>
        <label htmlFor="back" className="block text-sm font-medium text-gray-700">
          Back (Answer)
        </label>
        <textarea
          id="back"
          value={back}
          onChange={(e) => setBack(e.target.value)}
          required
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder="Enter the answer..."
        />
      </div>

      <div>
        <label htmlFor="language" className="block text-sm font-medium text-gray-700">
          Language (for text-to-speech)
        </label>
        <select
          id="language"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="en">English</option>
          <option value="es">Spanish</option>
          <option value="fr">French</option>
          <option value="de">German</option>
          <option value="it">Italian</option>
          <option value="pt">Portuguese</option>
          <option value="ru">Russian</option>
          <option value="ja">Japanese</option>
          <option value="ko">Korean</option>
          <option value="zh">Chinese</option>
        </select>
      </div>

      <div>
        <label htmlFor="level" className="block text-sm font-medium text-gray-700">
          Language Level
        </label>
        <select
          id="level"
          value={level}
          onChange={(e) => setLevel(e.target.value as LanguageLevel)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="A1">A1 - Beginner</option>
          <option value="A2">A2 - Elementary</option>
          <option value="B1">B1 - Intermediate</option>
          <option value="B2">B2 - Upper Intermediate</option>
          <option value="C1">C1 - Advanced</option>
          <option value="C2">C2 - Proficient</option>
        </select>
      </div>

      <div>
        <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
          Tags (comma-separated)
        </label>
        <input
          id="tags"
          type="text"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder="vocabulary, chapter-1, verbs..."
        />
      </div>

      <div>
        <label htmlFor="order" className="block text-sm font-medium text-gray-700">
          Order (optional - for sequential study)
        </label>
        <input
          id="order"
          type="number"
          value={order}
          onChange={(e) => setOrder(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder="Leave blank for regular cards, or enter 1, 2, 3..."
        />
        <p className="mt-1 text-sm text-gray-500">
          Use for content that should be studied in a specific sequence (scripts, podcasts, stories)
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
      >
        {loading ? 'Saving...' : initialData ? 'Update Card' : 'Create Card'}
      </button>
    </form>
  )
}